/**
 * chunkUploader.js — drains the offline IndexedDB audio-chunk queue.
 *
 * Called:
 *   • right after a failed chunk POST
 *   • whenever the browser fires `online`
 *   • on app load (in case a prior session still has queued chunks)
 *   • when the service worker posts { type: 'DRAIN_CHUNKS' } after a Background Sync
 *
 * MAX_ATTEMPTS prevents infinite retry storms for chunks the server
 * keeps rejecting (e.g. auth revoked, session deleted).
 */
import { bumpAttempts, listChunks, removeChunk } from './chunkQueue';

const MAX_ATTEMPTS = 8;

let draining = false; // module-level guard so concurrent calls coalesce

export async function drainQueue(apiBase, { onChange } = {}) {
  if (draining) return { drained: 0, remaining: undefined, skipped: true };
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return { drained: 0, remaining: undefined, skipped: true };
  }
  draining = true;
  let drained = 0;
  try {
    const chunks = await listChunks();
    // Oldest first — preserve transcript ordering
    chunks.sort((a, b) => a.createdAt - b.createdAt);

    for (const rec of chunks) {
      if ((rec.attempts || 0) >= MAX_ATTEMPTS) {
        await removeChunk(rec.id);
        continue;
      }
      try {
        const form = new FormData();
        form.append('audio', rec.blob, `chunk_${rec.chunkIndex}.${rec.ext || 'webm'}`);
        const res = await fetch(
          `${apiBase}/sessions/${rec.sessionId}/transcribe-chunk?chunk_index=${rec.chunkIndex}`,
          {
            method: 'POST',
            body: form,
            headers: { Authorization: `Bearer ${rec.token}` },
          }
        );
        if (res.ok) {
          await removeChunk(rec.id);
          drained += 1;
          onChange?.({ id: rec.id, sessionId: rec.sessionId, status: 'done' });
        } else if (res.status === 401 || res.status === 403) {
          // Auth invalid — no point retrying with this token.
          await removeChunk(rec.id);
          onChange?.({ id: rec.id, sessionId: rec.sessionId, status: 'dropped' });
        } else {
          await bumpAttempts(rec.id);
          onChange?.({ id: rec.id, sessionId: rec.sessionId, status: 'retry' });
        }
      } catch {
        // Network error — bump and leave queued for next drain
        await bumpAttempts(rec.id);
        onChange?.({ id: rec.id, sessionId: rec.sessionId, status: 'retry' });
      }
    }
    const remaining = (await listChunks()).length;
    return { drained, remaining, skipped: false };
  } finally {
    draining = false;
  }
}
